/**
 * Conferma della revoca di una fattura in bozza.
 *
 * Sostituisce un window.confirm che diceva il falso. Il vecchio testo era uno
 * solo per tutti i casi e prometteva: "L'ordine di origine tornera' NON
 * fatturato (potrai rigenerare la fattura corretta)". Su una fattura DIRETTA
 * l'ordine di origine non esiste: non c'e' niente da cui rigenerare, e la
 * promessa induceva a premere. E' cosi' che il 2026-09-14 e' andata persa una
 * fattura diretta in bozza.
 *
 * Perche' un Dialog e non window.confirm, su un'azione irreversibile:
 * - i pulsanti nativi sono "OK" e "Annulla": l'etichetta non descrive l'azione,
 *   quindi si clicca a memoria;
 * - non si puo' aggiungere la frizione che serve (digitare il numero), ne'
 *   distinguere il pulsante distruttivo;
 * - dopo il secondo dialog alcuni browser offrono "impedisci a questa pagina di
 *   creare altre finestre": la conferma piu' importante del prodotto puo'
 *   smettere di comparire.
 * WCAG 2.2 SC 3.3.4 (AA) chiede, per operazioni con effetti legali o
 * finanziari, almeno una fra reversibile / verificata / confermata con
 * possibilita' di revisione. Qui la reversibilita' non c'e': la conferma con
 * revisione non e' un vezzo, e' l'unica delle tre che possiamo dare.
 *
 * Sul NUMERO il testo dice il vero e non promette: il servizio prova a
 * rilasciarlo solo se e' l'ultimo della serie, ma al 2026-09-14 quel rilascio
 * non avviene mai (_rilascia_numero_fattura_se_ultimo interroga
 * contatori_fattura senza filtrare per `serie` e legge la riga sbagliata).
 * Verificato sul campo: numeri revocati in sequenza non sono mai stati
 * riassegnati. Finche' non e' corretto, qui si scrive che il numero resta
 * consumato, che e' quello che succede davvero.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Numero della fattura; null se la bozza non l'ha ancora. */
  numero: string | null;
  /** true solo se la fattura nasce da un ordine di vendita agganciato. */
  daOrdineVendita: boolean;
  /** true mentre la revoca e' in corso: il chiamante possiede l'operazione. */
  inCorso: boolean;
  onConferma: () => void;
}

export function RevocaBozzaDialog({
  open,
  onOpenChange,
  numero,
  daOrdineVendita,
  inCorso,
  onConferma,
}: Props) {
  // Cio' che va digitato per sbloccare: il numero se c'e', altrimenti REVOCA.
  const atteso = numero ?? 'REVOCA';
  const [digitato, setDigitato] = useState('');

  useEffect(() => {
    if (open) setDigitato('');
  }, [open]);

  const corrisponde = digitato.trim() === atteso;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Revocare la bozza {numero ? `n. ${numero}` : 'senza numero'}?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {daOrdineVendita ? (
            <p className="text-sm">
              Questa bozza viene eliminata.{' '}
              <strong>Non potrai piu&apos; recuperarla dall&apos;interfaccia.</strong>
            </p>
          ) : (
            <p className="text-sm">
              <strong>
                Questa fattura non nasce da un ordine: non esiste nessun documento da cui
                rigenerarla.
              </strong>{' '}
              Eliminandola perdi i dati che hai inserito, righe comprese, e per rifarla
              dovrai reinserirli da zero.{' '}
              <strong>Non potrai piu&apos; recuperarla dall&apos;interfaccia.</strong>
            </p>
          )}

          <div className="text-sm">
            <p className="font-medium">Cosa succede:</p>
            <ul className="list-disc pl-5 space-y-1">
              {daOrdineVendita && (
                <li>
                  l&apos;ordine di vendita di origine torna NON fatturato: da li&apos;
                  potrai rigenerare la fattura;
                </li>
              )}
              <li>le scadenze di incasso collegate a questa bozza vengono annullate;</li>
              <li>
                il numero {numero ?? 'assegnato'} resta consumato: la prossima fattura
                prendera&apos; il numero successivo e nella numerazione restera&apos; un
                salto, che e&apos; previsto e spiegato dalla revoca;
              </li>
              <li>l&apos;operazione resta registrata e tracciata.</li>
            </ul>
          </div>

          <p className="text-sm text-muted-foreground">
            Se ti serve CORREGGERE questa bozza e non buttarla, chiudi: data,
            controparte e P.IVA si cambiano con &quot;Modifica dati&quot;, mentre
            righe, importi e dati di pagamento con &quot;Modifica righe e
            importi&quot;. La revoca non e&apos; una correzione.
          </p>

          <div>
            <Label htmlFor="revoca-conferma">
              Per confermare, scrivi {numero ? `il numero della fattura: ` : ''}
              <strong>{atteso}</strong>
            </Label>
            <Input
              id="revoca-conferma"
              value={digitato}
              onChange={(e) => setDigitato(e.target.value)}
              disabled={inCorso}
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={inCorso}
          >
            Annulla
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConferma}
            disabled={inCorso || !corrisponde}
          >
            {inCorso ? 'Revoca in corso...' : 'Revoca definitivamente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RevocaBozzaDialog;
